import _ from 'lodash';
import { renderToStaticMarkup } from 'react-dom/server';
import * as Diff from 'diff';
import classnames from 'classnames';
import moment from 'moment-timezone';
import parse from 'html-react-parser';
import React, { Fragment, useState } from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAndCopyButton from 'widgets/click-and-copy-button';
import FormControlLabel from '@mui/material/FormControlLabel';
import makeStyles from '@mui/styles/makeStyles';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
/** import mui components in the section above, Sort Lines Ascending */

function getFormatDate(entryDate, organizationTimezone) {
    const dayMonthYear = moment(entryDate)
        .tz(organizationTimezone)
        .format('MMM DD, YYYY');
    const hoursMinutesSeconds = moment(entryDate)
        .tz(organizationTimezone)
        .format('HH:mm:ss');
    const timezoneAbbreviation = `(${moment()
        .tz(organizationTimezone)
        .format('z')})`;
    return dayMonthYear + ' ' + hoursMinutesSeconds + ' ' + timezoneAbbreviation;
}

function getUpdatedItem(fields, entry) {
    const updateType = _.capitalize(entry.action);
    if (entry.action === 'created') {
        return updateType;
    }

    const updatedFieldNames = fields
        .filter(field => field.hasChange(entry) && !field.isCategory)
        .map(field => field.label.replace(/,$/, ''));

    if (updatedFieldNames.length <= 5) {
        return `${updateType} ${updatedFieldNames.join(', ')}`;
    }

    const displayedFields = updatedFieldNames.slice(0, 4).join(', ');
    const remainingFieldsCount = updatedFieldNames.length - 4;

    return `${updateType} ${displayedFields}, and ${remainingFieldsCount} other fields`;
}

export function getUpdatedBy(user) {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
    if (!user.email) {
        return name;
    }
    return name + '\n(' + user.email + ')';
}

/**
 * Copy button for one history row.
 * A string `clipboardText` is copied as-is, so creative and list line breaks survive.
 * Other fields still have HTML tags stripped from the visible text.
 *
 * @param {Object} changedData - Row data, including `curr` and an optional `clipboardText`.
 * @param {Object} classes - MUI classes for the overlay button.
 * @returns {JSX.Element}
 */
function renderClickAndCopyButton(changedData, classes) {
    let textToCopy = changedData.clipboardText;
    if (typeof textToCopy !== 'string') {
        textToCopy = changedData.curr;
        if (changedData.label.indexOf('HTML') === -1) {
            textToCopy = textToCopy.replace(/(<([^>]+)>)/gi, '');
        }
    }
    return (
        <div className={classes.copyButton}>
            <ClickAndCopyButton textToCopy={textToCopy} textToDisplay="Copy" />
        </div>
    );
}

function setChangeStyle(value, change) {
    let returnString = value;
    if (change === 'added') {
        returnString = '<span style="background-color:#e0f2f1;">' + returnString + '</span>';
    } else if (change === 'removed') {
        returnString = '<span style="background-color:#f6bcbb;">' + returnString + '</span>';
    }
    return returnString;
}

/**
 * Splits a diff hunk into lines, dropping the trailing newline diffLines keeps.
 *
 * @param {string} value - Hunk text from `Diff.diffLines`.
 * @returns {string[]} Lines in the hunk.
 */
function hunkLines(value) {
    const trimmed = value.endsWith('\n') ? value.slice(0, -1) : value;
    if (trimmed === '') {
        return [];
    }
    return trimmed.split('\n');
}

/**
 * Finds leaf `<div>` blocks. Nested wrappers are left in place so each visual row can be diffed.
 *
 * @param {string} html - Static history markup.
 * @returns {{ inner: string }[]} Leaf divs in document order.
 */
function leafDivs(html) {
    const rows = [];
    const pattern = /<div>((?:(?!<div)[\s\S])*?)<\/div>/g;
    let match = pattern.exec(html);
    while (match) {
        rows.push({ inner: match[1] });
        match = pattern.exec(html);
    }
    return rows;
}

/**
 * Writes highlighted leaf contents back into the original markup.
 *
 * @param {string} html - Original static markup.
 * @param {string[]} inners - Replacement inner HTML, one per leaf div.
 * @returns {string} Markup with leaf contents replaced.
 */
function replaceLeafInners(html, inners) {
    let index = 0;
    return html.replace(/<div>((?:(?!<div)[\s\S])*?)<\/div>/g, () => {
        const inner = inners[index];
        index += 1;
        return `<div>${inner}</div>`;
    });
}

/**
 * Highlights only the words that differ, using the End Date span colors.
 * A missing side is an empty string so `diffWords` is never called on undefined.
 *
 * @param {string} prevText - Previous visible text.
 * @param {string} currText - Current visible text.
 * @returns {{ prev: string, curr: string }} Both sides with change spans inserted.
 */
function highlightWords(prevText, currText) {
    const previous = typeof prevText === 'string' ? prevText : '';
    const current = typeof currText === 'string' ? currText : '';
    const diff = Diff.diffWords(previous, current);
    let prev = '';
    let curr = '';
    diff.forEach(part => {
        if (part.added) {
            curr += setChangeStyle(part.value, 'added');
        } else if (part.removed) {
            prev += setChangeStyle(part.value, 'removed');
        } else {
            prev += part.value;
            curr += part.value;
        }
    });
    return { prev, curr };
}

/**
 * Reads one leaf when the index is inside the list.
 *
 * @param {string[]} inners - Leaf contents.
 * @param {number} index - Leaf index.
 * @returns {string|undefined} The leaf, or undefined past the end.
 */
function leafAt(inners, index) {
    if (index < 0 || index >= inners.length) {
        return undefined;
    }
    return inners[index];
}

/**
 * Paints a whole leaf green or red when that leaf exists.
 *
 * @param {string[]} inners - Leaf contents, updated in place.
 * @param {number} index - Leaf index.
 * @param {string} change - `added` or `removed`.
 * @returns {void}
 */
function paintWholeLeaf(inners, index, change) {
    const leaf = leafAt(inners, index);
    if (typeof leaf !== 'string') {
        return;
    }
    inners[index] = setChangeStyle(leaf, change);
}

/**
 * Word-highlights creative history HTML without painting an entire line.
 * A leaf whose text contains newlines is one block. Equal leaf counts are
 * compared as whole strings so a shared prefix cannot walk off the list.
 *
 * @param {string} prevHtml - Previous static markup.
 * @param {string} currHtml - Current static markup.
 * @returns {{ prev: string, curr: string }|null} Highlighted markup, or null when there are no leaf divs.
 */
function highlightHtmlWords(prevHtml, currHtml) {
    const prevRows = leafDivs(prevHtml);
    const currRows = leafDivs(currHtml);
    if (prevRows.length === 0 && currRows.length === 0) {
        return null;
    }

    const prevInners = prevRows.map(row => row.inner);
    const currInners = currRows.map(row => row.inner);
    const anyLeafHasNewline =
        prevInners.some(inner => inner.includes('\n')) ||
        currInners.some(inner => inner.includes('\n'));

    if (anyLeafHasNewline && prevInners.length === currInners.length) {
        for (let index = 0; index < prevInners.length; index += 1) {
            const words = highlightWords(prevInners[index], currInners[index]);
            prevInners[index] = words.prev;
            currInners[index] = words.curr;
        }
        return {
            prev: replaceLeafInners(prevHtml, prevInners),
            curr: replaceLeafInners(currHtml, currInners),
        };
    }

    // One line per leaf. Newlines inside a leaf must not advance the row index.
    const prevLines = anyLeafHasNewline
        ? prevInners.map(inner => inner.split('\n').join('\u0000'))
        : prevInners;
    const currLines = anyLeafHasNewline
        ? currInners.map(inner => inner.split('\n').join('\u0000'))
        : currInners;
    const parts = Diff.diffLines(`${prevLines.join('\n')}\n`, `${currLines.join('\n')}\n`);
    let prevIndex = 0;
    let currIndex = 0;

    for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        const lines = hunkLines(part.value);
        if (!part.added && !part.removed) {
            prevIndex += lines.length;
            currIndex += lines.length;
            continue;
        }

        const next = parts[index + 1];
        if (part.removed && next && next.added) {
            const removedLines = lines;
            const addedLines = hunkLines(next.value);
            const pairCount = Math.min(removedLines.length, addedLines.length);
            for (let pair = 0; pair < pairCount; pair += 1) {
                const prevLeaf = leafAt(prevInners, prevIndex);
                const currLeaf = leafAt(currInners, currIndex);
                if (typeof prevLeaf === 'string' && typeof currLeaf === 'string') {
                    const words = highlightWords(prevLeaf, currLeaf);
                    prevInners[prevIndex] = words.prev;
                    currInners[currIndex] = words.curr;
                } else {
                    paintWholeLeaf(prevInners, prevIndex, 'removed');
                    paintWholeLeaf(currInners, currIndex, 'added');
                }
                prevIndex += 1;
                currIndex += 1;
            }
            for (let extra = pairCount; extra < removedLines.length; extra += 1) {
                paintWholeLeaf(prevInners, prevIndex, 'removed');
                prevIndex += 1;
            }
            for (let extra = pairCount; extra < addedLines.length; extra += 1) {
                paintWholeLeaf(currInners, currIndex, 'added');
                currIndex += 1;
            }
            index += 1;
            continue;
        }

        if (part.removed) {
            lines.forEach(() => {
                paintWholeLeaf(prevInners, prevIndex, 'removed');
                prevIndex += 1;
            });
        }
        if (part.added) {
            lines.forEach(() => {
                paintWholeLeaf(currInners, currIndex, 'added');
                currIndex += 1;
            });
        }
    }

    return {
        prev: replaceLeafInners(prevHtml, prevInners),
        curr: replaceLeafInners(currHtml, currInners),
    };
}

export function getPrevCurrentComparison(dataObj) {
    let returnObj = dataObj;
    if (returnObj.prev === undefined) return;
    let prev =
            typeof returnObj.prevState === 'object'
                ? renderToStaticMarkup(returnObj.prevState)
                : returnObj.prevState,
        curr =
            typeof returnObj.current === 'object'
                ? renderToStaticMarkup(returnObj.current)
                : returnObj.current;

    let diff,
        updatedValue = '',
        isDiv = false;
    if (/<[a-z][\s\S]*>/i.test(curr)) {
        if (returnObj.highlight === 'words') {
            const highlighted = highlightHtmlWords(String(prev), String(curr));
            if (highlighted) {
                returnObj.prevState = highlighted.prev;
                returnObj.current = highlighted.curr;
                return returnObj;
            }
        }
        diff = Diff.diffTrimmedLines(prev, curr);
        isDiv = true;
    } else {
        diff = Diff.diffWords(prev, curr);
    }

    for (let i = 0; i < diff.length; i++) {
        const wordHasNoChange = !diff[i]['removed'] && !diff[i]['added'];
        const hasWordBeenAdded = diff[i]['added'];
        const hasWordBeenRemoved = diff[i]['removed'];

        if (hasWordBeenAdded) {
            diff[i]['currExist'] = true;
        }

        if (hasWordBeenRemoved) {
            diff[i]['prevExist'] = true;
        }

        if (wordHasNoChange) {
            diff[i]['currExist'] = true;
            diff[i]['prevExist'] = true;
        }
    }

    if (!isDiv) {
        let currentTemp = '',
            prevTemp = '';
        for (let j = 0; j < diff.length; j++) {
            if (diff[j]['prevExist'] && diff[j]['currExist']) {
                prevTemp += diff[j]['value'];
                currentTemp += diff[j]['value'];
            }
            if (diff[j]['prevExist'] && !diff[j]['currExist']) {
                prevTemp += diff[j]['added']
                    ? setChangeStyle(diff[j]['value'], 'added')
                    : diff[j]['removed']
                    ? setChangeStyle(diff[j]['value'], 'removed')
                    : diff[j]['value'];
            }
            if (diff[j]['currExist'] && !diff[j]['prevExist']) {
                currentTemp += diff[j]['added']
                    ? setChangeStyle(diff[j]['value'], 'added')
                    : diff[j]['removed']
                    ? setChangeStyle(diff[j]['value'], 'removed')
                    : diff[j]['value'];
            }
        }
        returnObj.prevState = prevTemp;
        returnObj.current = currentTemp;
    } else {
        diff.forEach(function(str) {
            if (str.added) {
                updatedValue = '<div style="background-color:#e0f2f1;">' + str.value + '</div>';
                if (curr.indexOf(str.value) !== -1) {
                    returnObj.current = curr.replace(str.value, updatedValue);
                } else if (prev.indexOf(str.value) !== -1) {
                    returnObj.prevState = prev.replace(str.value, updatedValue);
                }
            } else if (str.removed) {
                updatedValue = '<div style="background-color:#f6bcbb;">' + str.value + '</div>';
                if (curr.indexOf(str.value) !== -1) {
                    returnObj.current = curr.replace(str.value, updatedValue);
                } else if (prev.indexOf(str.value) !== -1) {
                    returnObj.prevState = prev.replace(str.value, updatedValue);
                }
            }
        });
    }
    return returnObj;
}

/**
 * Converts a field format value into a string safe for history diff rendering.
 *
 * Arrays must be joined before the generic object branch: in JavaScript arrays
 * are objects, and passing them to renderToStaticMarkup produces empty output.
 *
 * @param {*} value - Raw return value from a history field's format()
 * @returns {string|*} String representation, or the original value for primitives
 */
function stringifyFieldValue(value) {
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    if (value !== null && typeof value === 'object') {
        return renderToStaticMarkup(value);
    }
    if (typeof value === 'number') {
        return value.toString();
    }
    return value;
}

export function renderTableRow(
    fields,
    historyEntries,
    selectedRow,
    classes,
    isFold,
    isProspectMedia = false
) {
    const [mouseOnRow, setMouseOnRow] = useState(null);
    let changedData = [],
        dataObj = {},
        isPrevCompare = false;

    _.map(fields, field => {
        if (historyEntries[selectedRow] === undefined) return;
        dataObj.isCategory = field.isCategory;
        if (isFold) {
            // show only changes
            if (field.hasChange(historyEntries[selectedRow])) {
                dataObj.label = field.label;
                dataObj.highlight = field.highlight;
                if (selectedRow + 1 < historyEntries.length) {
                    const selectedEntry = historyEntries[selectedRow];
                    // Custom fields supply formatPrevious so From uses entry._previousSnapshot.
                    dataObj.prevState = field.formatPrevious
                        ? field.formatPrevious(selectedEntry)
                        : field.format(historyEntries[selectedRow + 1]);
                    dataObj.prevState = stringifyFieldValue(dataObj.prevState);
                    isPrevCompare = true;
                }
                // Join arrays before object/React markup conversion (see stringifyFieldValue).
                dataObj.current = stringifyFieldValue(field.format(historyEntries[selectedRow]));
                // Store the multiline copy text before the row is rendered. The button reads it later.
                if (field.useClipboardText && typeof field.getClipboardText === 'function') {
                    const clipboardText = field.getClipboardText(historyEntries[selectedRow]);
                    if (typeof clipboardText === 'string') {
                        dataObj.clipboardText = clipboardText;
                    }
                }

                dataObj.prev =
                    typeof dataObj.prevState === 'object'
                        ? renderToStaticMarkup(dataObj.prevState)
                        : typeof dataObj.prevState === 'number'
                        ? dataObj.prevState.toString()
                        : dataObj.prevState;

                dataObj.curr =
                    typeof dataObj.current === 'object'
                        ? renderToStaticMarkup(dataObj.current)
                        : typeof dataObj.current === 'number'
                        ? dataObj.current.toString()
                        : dataObj.current;

                if (isPrevCompare) {
                    const compared = getPrevCurrentComparison(dataObj);
                    // getPrevCurrentComparison returns undefined when prev is missing; keep dataObj.
                    if (compared) {
                        dataObj = compared;
                    }
                }
                // Default: skip rows with identical From/To. Custom fields opt in via keepDetailRowWhenUnchanged.
                const shouldRenderRow =
                    dataObj &&
                    (dataObj.curr !== dataObj.prev ||
                        dataObj.isCategory ||
                        field.keepDetailRowWhenUnchanged);
                if (shouldRenderRow) {
                    changedData.push(dataObj);
                }
                dataObj = {};
            }
        } else {
            // show history snapshot
            dataObj.label = field.label;
            // Store the multiline copy text before the row is rendered. The button reads it later.
            if (field.useClipboardText && typeof field.getClipboardText === 'function') {
                const clipboardText = field.getClipboardText(historyEntries[selectedRow]);
                if (typeof clipboardText === 'string') {
                    dataObj.clipboardText = clipboardText;
                }
            }
            let formatData =
                typeof field.format(historyEntries[selectedRow]) === 'object'
                    ? renderToStaticMarkup(field.format(historyEntries[selectedRow]))
                    : typeof field.format(historyEntries[selectedRow]) === 'number'
                    ? field.format(historyEntries[selectedRow]).toString()
                    : field.format(historyEntries[selectedRow]);
            if (field.hasChange(historyEntries[selectedRow])) {
                let formatStr = formatData.replace(/<[^>]*>/g, '');
                dataObj.current = formatData.replace(
                    formatStr,
                    '<span style="background-color:#e0f2f1;">' + formatStr + '</span>'
                );
            } else {
                if (Array.isArray(formatData)) {
                    formatData = formatData.join(', ');
                }
                dataObj.current = formatData;
            }
            changedData.push(dataObj);
            dataObj = {};
        }
    });

    return (
        <Fragment>
            {_.map(changedData, function(changedData, index) {
                let currentData =
                    typeof changedData.current === 'object'
                        ? renderToStaticMarkup(changedData.current)
                        : changedData.current;

                const shouldNotRenderRow =
                    isProspectMedia &&
                    (changedData.label === 'Brand Safety' ||
                        changedData.label === 'Fraud Prevention');
                /**
                 * Content HTML is escaped markup. Parse it so the pre-wrap wrapper keeps
                 * the source line breaks instead of showing one condensed string.
                 */
                const renderFieldValue = value => {
                    if (typeof value !== 'string' || value === '') {
                        return value;
                    }
                    const rendered = parse(value);
                    if (changedData.label.indexOf('HTML') === -1) {
                        return rendered;
                    }
                    return <div className={classes.htmlSource}>{rendered}</div>;
                };
                return (
                    !shouldNotRenderRow && (
                        <tr className={classes.tableTr} key={index}>
                            {changedData.isCategory ? (
                                <td className={classes.tableTdLeftCategorized}>
                                    {changedData.label}
                                </td>
                            ) : (
                                <td className={classes.tableTdLeft}>{changedData.label}</td>
                            )}
                            {isPrevCompare && !changedData.isCategory ? (
                                <td className={classes.tableTdRight}>
                                    <div className={classes.fromDiv}>
                                        <div className={classes.fromToLabel}>From:</div>
                                        <div className={classes.changed}>
                                            {renderFieldValue(changedData.prevState)}
                                        </div>
                                    </div>
                                    <div
                                        className={classes.toDiv}
                                        onMouseOver={() => {
                                            setMouseOnRow(index);
                                        }}
                                        onMouseLeave={() => {
                                            setMouseOnRow(null);
                                        }}
                                    >
                                        <div className={classes.fromToLabel}>To:</div>
                                        <div className={classes.current}>
                                            {renderFieldValue(currentData)}
                                            {mouseOnRow === index &&
                                                renderClickAndCopyButton(changedData, classes)}
                                        </div>
                                    </div>
                                </td>
                            ) : isFold && !changedData.isCategory ? (
                                // Created entries have no From/To row. Hover here shows Copy over these values.
                                <td
                                    className={classes.tableTdRightCreated}
                                    onMouseOver={() => {
                                        setMouseOnRow(index);
                                    }}
                                    onMouseLeave={() => {
                                        setMouseOnRow(null);
                                    }}
                                >
                                    <span>{renderFieldValue(currentData)}</span>
                                    {mouseOnRow === index &&
                                        currentData !== undefined &&
                                        currentData !== '' &&
                                        renderClickAndCopyButton(changedData, classes)}
                                </td>
                            ) : (
                                <td className={classes.tableTdRight}>
                                    {currentData !== undefined && renderFieldValue(currentData)}
                                    {currentData !== undefined &&
                                        currentData !== '' &&
                                        (mouseOnRow === index &&
                                            renderClickAndCopyButton(changedData, classes))}
                                </td>
                            )}
                        </tr>
                    )
                );
            })}
        </Fragment>
    );
}

const useStyles = makeStyles(() => ({
    historyTable: {
        height: 600,
    },
    tableTitle: {
        fontSize: 20,
    },
    titleCreativeId: {
        fontSize: 14,
    },
    // Fills the dialog under the title so the panes reach the Close bar.
    historyRoot: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        height: '100%',
    },
    cardWrapper: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden',
    },
    // Side-by-side panes. Stretch so both columns use the card height.
    historyColumns: {
        display: 'flex',
        alignItems: 'stretch',
        flex: '1 1 auto',
        minHeight: 0,
    },
    leftMenu: {
        overflowY: 'auto',
        flex: '0 0 250px',
        width: 250,
        maxWidth: 250,
        minWidth: 0,
        overflowX: 'hidden',
        minHeight: 0,
    },
    rightContents: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0 10px',
        overflow: 'hidden',
        width: '100%',
        flex: '1 1 auto',
        minHeight: 0,
    },
    eachEntry: {
        minHeight: 95,
        padding: 16,
        fontSize: 14,
        width: '100%',
        overflowWrap: 'anywhere',
        wordBreak: 'normal',
        borderBottom: '1pt solid #E0E0E0',
        '&:hover': {
            backgroundColor: '#F2F2F2',
            cursor: 'pointer',
        },
    },
    entrySelected: {
        padding: 16,
        borderLeft: 'solid 6px #009688',
        backgroundColor: '#F2F2F2',
    },
    entryLI: {
        lineHeight: 1.3,
    },
    entryDate: {
        fontWeight: 500,
    },
    table: {
        width: '100%',
        flex: '1 1 auto',
        minHeight: 0,
        height: '100%',

        '& #ad #container': {
            position: 'relative',
            margin: 0,
        },
    },
    tBody: {
        display: 'block',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    tableTr: {
        borderBottom: '1pt solid #E0E0E0',
        verticalAlign: 'middle',
    },
    // Wide enough for titles such as Conversion Optimization to stay on one line.
    tableTdLeftCategorized: {
        width: 280,
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'left',
        paddingLeft: 4,
        paddingTop: 15,
        paddingBottom: 15,
        wordBreak: 'normal',
        whiteSpace: 'nowrap',
    },
    // Same width as the category titles, so labels such as Optimized Conversion Events stay on one line.
    tableTdLeft: {
        width: 280,
        fontSize: 14,
        textAlign: 'left',
        paddingLeft: 4,
        paddingTop: 15,
        paddingBottom: 15,
        wordBreak: 'normal',
        whiteSpace: 'nowrap',
    },
    // Fills the row beside the 280px label column. Relative so the Copy button can overlay a snapshot.
    tableTdRight: {
        width: '100%',
        fontSize: 14,
        verticalAlign: 'middle',
        textAlign: 'left',
        paddingTop: 12,
        paddingRight: 8,
        paddingBottom: 8,
        lineHeight: '17px',
        wordBreak: 'break-all',
        position: 'relative',
    },
    // Same fill as the value cell. Relative so the Copy button can overlay a created row.
    tableTdRightCreated: {
        position: 'relative',
        width: '100%',
        fontSize: 14,
        verticalAlign: 'middle',
        textAlign: 'left',
        paddingTop: 12,
        paddingRight: 8,
        paddingBottom: 8,
        paddingLeft: 8,
        lineHeight: '17px',
        wordBreak: 'break-all',
        backgroundColor: '#e0f2f1',
    },
    fromDiv: {
        display: 'block',
        minHeight: 24,
    },
    toDiv: {
        display: 'block',
        minHeight: 24,
        clear: 'both',
    },
    fromToLabel: {
        textAlign: 'right',
        paddingTop: 1,
        paddingRight: 8,
        float: 'left',
        fontWeight: 500,
        fontSize: 12,
        width: 45,
    },
    // Fills the space beside the 45px From label, plus its 8px padding.
    changed: {
        paddingBottom: 8,
        width: 'calc(100% - 53px)',
        float: 'left',
    },
    // Relative so the Copy button can sit on top of these values without shifting the row.
    current: {
        position: 'relative',
        paddingBottom: 8,
        width: 'calc(100% - 53px)',
        float: 'left',
    },
    // Keeps its height when the Created entry hides the switch, so the panes do not jump.
    tableToggleSwitch: {
        float: 'right',
        paddingRight: 20,
    },
    tableToggleSwitchHidden: {
        visibility: 'hidden',
    },
    // Keeps Content HTML source line breaks. The parent cell uses break-all.
    htmlSource: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        overflowWrap: 'anywhere',
    },
    // Overlay on the values being copied. Out of flow, so hover does not move the row.
    copyButton: {
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 1,
        color: '#fff',
        backgroundColor: '#4FC3F7',
        border: '1px solid #4FC3F7',
        cursor: 'pointer',
        borderRadius: 2,
    },
    progress: {
        display: 'flex',
        flex: '1 1 auto',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 170,
        color: '#009688',
    },
}));

/**
 * Extracts all field paths from a patch object, including both full nested paths and top-level fields.
 * @param {Object} patch - The patch object containing field changes
 * @returns {string[]} Array of unique field paths (e.g., ['automatic_bid_price.max_ecpm', 'automatic_bid_price'])
 */
function extractPatchFields(patch) {
    const fields = [];

    _.forEach(patch, (value, key) => {
        // Add the full path (e.g., 'automatic_bid_price.max_ecpm')
        fields.push(key);

        // Add the top-level field only if it's a nested path (e.g., 'automatic_bid_price' from 'automatic_bid_price.max_ecpm')
        const parts = key.split('.');
        if (parts.length > 1) {
            fields.push(parts[0]);
        }
    });

    return _.uniq(fields);
}

/**
 * Checks if any fields in the patch match the specified fields to compare.
 * Supports both top-level and nested field path matching.
 * @param {Object} patch - The patch object containing field changes
 * @param {string[]} fieldsToCompare - Array of field paths to check for modifications
 * @returns {boolean} True if any field in fieldsToCompare has been modified in the patch
 */
export function hasTopLevelFieldsBeenModified(patch, fieldsToCompare) {
    const patchFields = extractPatchFields(patch);
    const intersection = _.intersection(patchFields, fieldsToCompare);
    return intersection.length > 0;
}

export function HistoryTableV3(props) {
    const {
        title,
        secondaryTitle,
        historyEntries,
        fields,
        organizationTimezone,
        organizationId,
        isLoading,
    } = props;
    const [selectedRow, setSelectedRow] = useState(0);
    const [isFold, setIsFold] = useState(true);
    const classes = useStyles();
    const indexCreatedRow = historyEntries.length - 1;

    const SnapshotSwitch = withStyles({
        switchBase: {
            color: '#FAFAFC',
            '&$checked': {
                color: '#009688',
            },
            '&$checked + $track': {
                backgroundColor: '#009688',
            },
        },
        checked: {},
        track: {},
    })(Switch);

    const isProspectMedia = organizationId === '61ba37fb834aef8e532e0687';
    const tableRows = (
        <div className={classes.rightContents}>
            <table className={classes.table}>
                <tbody className={classes.tBody}>
                    {renderTableRow(
                        fields,
                        historyEntries,
                        selectedRow,
                        classes,
                        isFold,
                        isProspectMedia
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className={classes.historyRoot}>
            <Box margin="20px 0" className={classes.tableTitle}>
                {title}
            </Box>
            <Box
                marginBottom="10px"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <div>
                    <span className={classes.titleCreativeId}>{secondaryTitle}</span>
                </div>
                {/* Stay mounted on the Created entry so hiding the switch does not shift the panes. */}
                <div
                    className={classnames(classes.tableToggleSwitch, {
                        [classes.tableToggleSwitchHidden]: indexCreatedRow === selectedRow,
                    })}
                >
                    <FormControlLabel
                        control={
                            <SnapshotSwitch
                                checked={!isFold}
                                disabled={indexCreatedRow === selectedRow}
                                onChange={() => setIsFold(!isFold)}
                            />
                        }
                        label="Show history snapshots"
                        labelPlacement="start"
                    />
                </div>
            </Box>
            <Card className={classes.cardWrapper}>
                {isLoading && (
                    <div className={classes.progress}>
                        <CircularProgress color="inherit" />
                    </div>
                )}
                {!isLoading && historyEntries.length === 0 && (
                    <div className={classes.progress}>
                        <Typography align="center">No history found</Typography>
                    </div>
                )}
                {!isLoading && historyEntries.length !== 0 && (
                    <Box className={classes.historyColumns}>
                        <div className={classes.leftMenu}>
                            {_.map(historyEntries, (entry, index) => {
                                return (
                                    <ul
                                        key={index}
                                        className={classnames(
                                            classes.eachEntry,
                                            selectedRow === index ? classes.entrySelected : ''
                                        )}
                                        onClick={() => {
                                            setSelectedRow(index);
                                            setIsFold(true);
                                        }}
                                    >
                                        <li
                                            className={classnames(
                                                classes.entryLI,
                                                classes.entryDate
                                            )}
                                        >
                                            {getFormatDate(entry.date, organizationTimezone)}
                                        </li>
                                        <li className={classes.entryLI}>
                                            {getUpdatedItem(fields, entry)}
                                        </li>
                                        <li className={classes.entryLI}>
                                            By {getUpdatedBy(entry.user)}
                                        </li>
                                    </ul>
                                );
                            })}
                        </div>
                        {tableRows}
                    </Box>
                )}
            </Card>
        </div>
    );
}
